/**
 * @fileoverview WebGPU-бэкенд: устройство, пайплайны, bind group'ы, цели рендера
 * @module MikhmelViewer/Cinematic10/WebGPUBackend
 *
 * Слой между декларативными описаниями проходов (wgslShaders/wgslCompute)
 * и низкоуровневым WebGPU API. Задачи:
 *
 *  • инициализация adapter/device с обработкой потери устройства
 *  • кэш пайплайнов и bind group layout'ов (создание пайплайна дорогое)
 *  • управление render targets с ленивым пересозданием при ресайзе
 *  • пул uniform-буферов (см. uniformLayout.js)
 *  • пинг-понг цели и история кадров для темпоральных эффектов
 *
 * Кэш bind group'ов ключуется по составу вьюх. При смене разрешения все
 * вьюхи пересоздаются, поэтому кэш инвалидируется целиком — иначе останутся
 * ссылки на уничтоженные текстуры.
 */

import { UniformLayout, UniformPool } from './uniformLayout.js';
import { RENDER_PASSES, buildShaderSource } from './wgslShaders.js';
import { COMPUTE_PASSES, COMPUTE_COMMON } from './wgslCompute.js';

const Ok = (value) => ({ success: true, value });
const Err = (error) => ({ success: false, error });

let _log = {
  debug: () => {}, info: () => {}, warn: () => {}, error: () => {},
};

/** Внедрить логгер приложения (по умолчанию — тишина). */
export function setBackendLogger(logger) {
  if (logger) _log = logger;
}

// ═══════════════════════════════════════════════════════════════════
// ФОРМАТЫ
// ═══════════════════════════════════════════════════════════════════

export const FORMATS = Object.freeze({
  /** HDR-промежуток. rgba16float фильтруется без доп. фич и пишется как storage. */
  HDR: 'rgba16float',
  /** LDR-промежуток. */
  LDR: 'rgba8unorm',
  /** Векторы движения: знаковые, нужен диапазон вне [0,1]. */
  VELOCITY: 'rgba16float',
  /** Одноканальные данные (AO, маски). */
  R: 'r16float',
});

const USAGE_FULL =
  GPUTextureUsage.TEXTURE_BINDING |
  GPUTextureUsage.RENDER_ATTACHMENT |
  GPUTextureUsage.COPY_SRC |
  GPUTextureUsage.COPY_DST;

const USAGE_STORAGE = USAGE_FULL | GPUTextureUsage.STORAGE_BINDING;

// ═══════════════════════════════════════════════════════════════════
// RENDER TARGET
// ═══════════════════════════════════════════════════════════════════

class RenderTarget {
  constructor(device, { name, width, height, format, storage = false }) {
    this.name = name;
    this.width = Math.max(1, width | 0);
    this.height = Math.max(1, height | 0);
    this.format = format;
    this.storage = storage;

    this.texture = device.createTexture({
      label: `rt:${name}`,
      size: { width: this.width, height: this.height, depthOrArrayLayers: 1 },
      format,
      usage: storage ? USAGE_STORAGE : USAGE_FULL,
    });
    this.view = this.texture.createView({ label: `rtv:${name}` });
  }

  destroy() {
    try { this.texture?.destroy(); } catch { /* уже уничтожена */ }
    this.texture = null;
    this.view = null;
  }
}

// ═══════════════════════════════════════════════════════════════════
// БЭКЕНД
// ═══════════════════════════════════════════════════════════════════

export class WebGPUBackend {
  constructor() {
    this.device = null;
    this.adapter = null;
    this.context = null;
    this.canvas = null;
    this.canvasFormat = 'bgra8unorm';

    this.limits = null;
    this.features = new Set();
    this.adapterInfo = null;

    this._renderPipelines = new Map();
    this._computePipelines = new Map();
    this._bindGroupLayouts = new Map();
    this._shaderModules = new Map();
    this._bindGroupCache = new Map();
    this._layouts = new Map();          // UniformLayout по имени прохода
    this._targets = new Map();
    this._samplers = new Map();
    this._storageBuffers = new Map();

    this._uniformPool = null;
    this._encoder = null;
    this._frameIndex = 0;
    this._initialized = false;
    this._destroyed = false;
    this._deviceLost = false;
    this._onDeviceLost = null;

    this._stats = { passes: 0, dispatches: 0, bindGroupsCreated: 0, pipelinesCreated: 0 };

    // [10.19.20] Захват ошибок валидации ПО ПРОХОДАМ — для диагностики.
    // Обычный onuncapturederror говорит «что-то невалидно», но не говорит, в
    // каком проходе. Диагностика включает захват, каждый runRenderPass /
    // runComputePass оборачивается в pushErrorScope, и отчёт называет виновника.
    this._захват = null;
  }

  /** Начать копить ошибки валидации с подписями проходов. */
  начатьЗахватОшибок() {
    this._захват = { записи: [], ожидания: [] };
  }

  /**
   * Закончить захват и отдать записи. Ждёт все отложенные popErrorScope —
   * ошибки отправки командного буфера приходят асинхронно.
   * @returns {Promise<Array<{место: string, ошибка: string}>>}
   */
  async завершитьЗахватОшибок() {
    const з = this._захват;
    this._захват = null;
    if (!з) return [];
    try { await Promise.all(з.ожидания); } catch (e) { з.записи.push({ место: 'сбор ошибок', ошибка: String(e?.message || e) }); }
    return з.записи;
  }

  /** Обернуть кусок кадра в error scope с подписью. Вне захвата — просто вызов. */
  _подЗахватом(место, действие) {
    const з = this._захват;
    if (!з || !this.device) return действие();
    this.device.pushErrorScope('validation');
    let итог;
    try {
      итог = действие();
    } finally {
      const п = this.device.popErrorScope()
        .then((ош) => { if (ош) з.записи.push({ место, ошибка: ош.message }); })
        .catch((e) => { з.записи.push({ место, ошибка: 'popErrorScope: ' + (e?.message || e) }); });
      з.ожидания.push(п);
    }
    return итог;
  }

  // ─── Инициализация ─────────────────────────────────────────────

  static isSupported() {
    return typeof navigator !== 'undefined' && !!navigator.gpu;
  }

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {object} options { powerPreference, onDeviceLost, requiredFeatures }
   */
  async init(canvas, options = {}) {
    if (this._initialized) return Ok(true);
    if (!WebGPUBackend.isSupported()) return Err('WebGPU not available in this browser');

    try {
      this.adapter = await navigator.gpu.requestAdapter({
        powerPreference: options.powerPreference || 'high-performance',
      });
      if (!this.adapter) return Err('No suitable GPU adapter found');

      // Запрашиваем только те фичи, которые адаптер реально отдаёт —
      // requestDevice с недоступной фичей падает целиком
      const wanted = options.requiredFeatures || ['timestamp-query', 'float32-filterable'];
      const requiredFeatures = wanted.filter(f => this.adapter.features.has(f));

      this.device = await this.adapter.requestDevice({
        label: 'cinematic-v10',
        requiredFeatures,
        requiredLimits: this._pickLimits(options.requiredLimits),
      });

      this.limits = this.device.limits;
      this.features = new Set(this.device.features);

      if (typeof this.adapter.requestAdapterInfo === 'function') {
        try { this.adapterInfo = await this.adapter.requestAdapterInfo(); } catch { /* необязательно */ }
      } else if (this.adapter.info) {
        this.adapterInfo = this.adapter.info;
      }

      // Потеря устройства: происходит при переключении GPU, засыпании,
      // сбросе драйвера. Без обработки приложение молча перестаёт рисовать.
      this._onDeviceLost = options.onDeviceLost || null;
      this.device.lost.then((info) => {
        if (this._destroyed) return;
        this._deviceLost = true;
        _log.warn(`[GPU] Device lost: ${info.reason} — ${info.message}`);
        if (this._onDeviceLost) this._onDeviceLost(info);
      });

      this.device.onuncapturederror = (event) => {
        _log.error('[GPU] Uncaptured error:', event.error?.message || event.error);
        if (this._захват) this._захват.записи.push({ место: 'вне охвата', ошибка: String(event.error?.message || event.error) });
      };

      this.canvas = canvas;
      this.context = canvas.getContext('webgpu');
      if (!this.context) return Err('Failed to acquire webgpu canvas context');

      this.canvasFormat = navigator.gpu.getPreferredCanvasFormat();
      this.context.configure({
        device: this.device,
        format: this.canvasFormat,
        alphaMode: options.alphaMode || 'premultiplied',
        usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC,
      });

      this._uniformPool = new UniformPool(this.device);
      this._createSamplers();
      this._initialized = true;

      _log.info(`[GPU] WebGPU готов — ${this.adapterInfo?.vendor || 'unknown'} ${this.adapterInfo?.architecture || ''}`);
      return Ok(true);
    } catch (e) {
      _log.error('[GPU] Init failed:', e);
      return Err(e.message || String(e));
    }
  }

  _pickLimits(requested) {
    if (!this.adapter) return undefined;
    const out = {};
    const defaults = {
      maxTextureDimension2D: 8192,
      maxStorageBufferBindingSize: 128 * 1024 * 1024,
      maxComputeWorkgroupStorageSize: 16384,
      ...(requested || {}),
    };
    // Просим не больше того, что даёт адаптер — иначе requestDevice отклонит
    for (const [key, value] of Object.entries(defaults)) {
      const supported = this.adapter.limits?.[key];
      if (typeof supported === 'number') out[key] = Math.min(value, supported);
    }
    return out;
  }

  _createSamplers() {
    this._samplers.set('linear', this.device.createSampler({
      label: 'samp:linear',
      magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge', addressModeW: 'clamp-to-edge',
    }));
    this._samplers.set('nearest', this.device.createSampler({
      label: 'samp:nearest',
      magFilter: 'nearest', minFilter: 'nearest', mipmapFilter: 'nearest',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge', addressModeW: 'clamp-to-edge',
    }));
    this._samplers.set('repeat', this.device.createSampler({
      label: 'samp:repeat',
      magFilter: 'linear', minFilter: 'linear',
      addressModeU: 'repeat', addressModeV: 'repeat',
    }));
  }

  getSampler(kind = 'linear') { return this._samplers.get(kind) || this._samplers.get('linear'); }
  isReady() { return this._initialized && !this._deviceLost && !this._destroyed; }
  /** [10.19.19] Потеряно ли устройство — чтобы плашка называла именно эту причину. */
  isDeviceLost() { return !!this._deviceLost; }
  getFrameIndex() { return this._frameIndex; }
  getStats() { return { ...this._stats }; }

  // ─── Шейдерные модули и пайплайны ──────────────────────────────

  _getShaderModule(key, code) {
    let mod = this._shaderModules.get(key);
    if (!mod) {
      mod = this.device.createShaderModule({ label: `shader:${key}`, code });
      this._shaderModules.set(key, mod);
    }
    return mod;
  }

  /** Layout uniform-структуры прохода (общий для render и compute). */
  getUniformLayout(passName) {
    let layout = this._layouts.get(passName);
    if (!layout) {
      const desc = RENDER_PASSES[passName] || COMPUTE_PASSES[passName];
      if (!desc) throw new Error(`[GPU] Unknown pass: ${passName}`);
      layout = new UniformLayout(desc.uniforms);
      this._layouts.set(passName, layout);
    }
    return layout;
  }

  _getRenderBindGroupLayout(passName) {
    let bgl = this._bindGroupLayouts.get(`r:${passName}`);
    if (bgl) return bgl;

    const desc = RENDER_PASSES[passName];
    // [10.19.20] ГЛУБИНА — НЕФИЛЬТРУЕМАЯ, И ЭТО НЕ ДЕТАЛЬ.
    //
    // Карта глубины хранится в r32float, а этот формат фильтруем ТОЛЬКО на
    // устройствах с возможностью float32-filterable. Раньше каждый слот здесь
    // объявлялся sampleType:'float' (фильтруемый) — на устройстве без этой
    // возможности привязка r32float к такому слоту невалидна, невалидный bind
    // group делает невалидным ВЕСЬ командный буфер, и кадра нет вовсе. Ровно
    // такие «Invalid CommandBuffer frame:N» были в журнале Андрея Вячеславовича.
    //
    // Глубина по устройству программы читается ТОЛЬКО без фильтрации
    // (sampNearest в DEPTH_HELPER; с 10.19.18 — намеренно), поэтому честное
    // объявление — 'unfilterable-float'. Фильтруемые форматы (rgba16float,
    // rgba8unorm) с таким объявлением тоже совместимы, так что запасная подача
    // sceneSrc вместо глубины не ломается. Заодно sampNearest (binding 2)
    // объявлен 'non-filtering' — он и есть нефильтрующий, а пара
    // «нефильтруемая текстура + фильтрующий сэмплер» запрещена на уровне
    // создания пайплайна.
    const entries = [
      { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
      { binding: 2, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'non-filtering' } },
    ];
    desc.textures.forEach((имя, i) => {
      const глубина = /Depth/.test(имя);
      entries.push({
        binding: 3 + i,
        visibility: GPUShaderStage.FRAGMENT,
        texture: { sampleType: глубина ? 'unfilterable-float' : 'float', viewDimension: '2d', multisampled: false },
      });
    });

    bgl = this.device.createBindGroupLayout({ label: `bgl:r:${passName}`, entries });
    this._bindGroupLayouts.set(`r:${passName}`, bgl);
    return bgl;
  }

  _getComputeBindGroupLayout(passName) {
    let bgl = this._bindGroupLayouts.get(`c:${passName}`);
    if (bgl) return bgl;

    const desc = COMPUTE_PASSES[passName];
    const entries = desc.bindings.map((b) => {
      const base = { binding: b.binding, visibility: GPUShaderStage.COMPUTE };
      switch (b.kind) {
        case 'uniform':
          return { ...base, buffer: { type: 'uniform' } };
        case 'texture':
          // [10.19.20] Депт-текстуры (r32float) нефильтруемы без отдельной
          // возможности устройства; компьюты читают их textureLoad, фильтрация
          // не нужна — объявляем честно (см. пояснение в рендерном layout).
          return { ...base, texture: { sampleType: /Depth/.test(b.name || '') ? 'unfilterable-float' : 'float', viewDimension: '2d' } };
        case 'texture3d':
          return { ...base, texture: { sampleType: 'float', viewDimension: '3d' } };
        case 'sampler':
          return { ...base, sampler: { type: 'filtering' } };
        case 'storageTexture':
          return { ...base, storageTexture: { access: 'write-only', format: b.format, viewDimension: '2d' } };
        case 'storageBuffer':
          return { ...base, buffer: { type: b.access === 'read-only' ? 'read-only-storage' : 'storage' } };
        default:
          throw new Error(`[GPU] Unknown binding kind: ${b.kind}`);
      }
    });

    bgl = this.device.createBindGroupLayout({ label: `bgl:c:${passName}`, entries });
    this._bindGroupLayouts.set(`c:${passName}`, bgl);
    return bgl;
  }

  getRenderPipeline(passName, targetFormat) {
    const key = `${passName}:${targetFormat}`;
    let pipeline = this._renderPipelines.get(key);
    if (pipeline) return pipeline;

    const module = this._getShaderModule(`r:${passName}`, buildShaderSource(passName));
    const bgl = this._getRenderBindGroupLayout(passName);

    pipeline = this.device.createRenderPipeline({
      label: `pipeline:${key}`,
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      vertex: { module, entryPoint: 'vs_main' },
      fragment: { module, entryPoint: 'fs_main', targets: [{ format: targetFormat }] },
      primitive: { topology: 'triangle-strip', stripIndexFormat: undefined, cullMode: 'none' },
    });

    this._renderPipelines.set(key, pipeline);
    this._stats.pipelinesCreated++;
    return pipeline;
  }

  getComputePipeline(passName) {
    let pipeline = this._computePipelines.get(passName);
    if (pipeline) return pipeline;

    const desc = COMPUTE_PASSES[passName];
    if (!desc) throw new Error(`[GPU] Unknown compute pass: ${passName}`);

    const module = this._getShaderModule(`c:${passName}`, desc.source);
    const bgl = this._getComputeBindGroupLayout(passName);

    pipeline = this.device.createComputePipeline({
      label: `compute:${passName}`,
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [bgl] }),
      compute: { module, entryPoint: desc.entryPoint },
    });

    this._computePipelines.set(passName, pipeline);
    this._stats.pipelinesCreated++;
    return pipeline;
  }

  // ─── Render targets ────────────────────────────────────────────

  /**
   * Создать или переиспользовать цель рендера. Если размер и формат
   * совпадают с существующей — возвращается она же.
   */
  ensureTarget(name, width, height, format = FORMATS.HDR, storage = false) {
    const w = Math.max(1, width | 0);
    const h = Math.max(1, height | 0);
    const existing = this._targets.get(name);

    if (existing && existing.width === w && existing.height === h &&
        existing.format === format && existing.storage === storage) {
      return existing;
    }

    if (existing) {
      existing.destroy();
      this._invalidateBindGroups();
    }

    const target = new RenderTarget(this.device, { name, width: w, height: h, format, storage });
    this._targets.set(name, target);
    return target;
  }

  getTarget(name) { return this._targets.get(name) || null; }

  destroyTarget(name) {
    const t = this._targets.get(name);
    if (t) { t.destroy(); this._targets.delete(name); this._invalidateBindGroups(); }
  }

  destroyAllTargets() {
    for (const t of this._targets.values()) t.destroy();
    this._targets.clear();
    this._invalidateBindGroups();
  }

  _invalidateBindGroups() {
    // Bind group держит ссылки на конкретные GPUTextureView. После
    // пересоздания текстур закэшированные группы указывают в никуда.
    this._bindGroupCache.clear();
  }

  // ─── Storage-буферы ────────────────────────────────────────────

  ensureStorageBuffer(name, byteLength, extraUsage = 0) {
    const size = Math.max(16, Math.ceil(byteLength / 4) * 4);
    let buf = this._storageBuffers.get(name);
    if (buf && buf.size >= size) return buf;

    if (buf) { buf.destroy(); this._invalidateBindGroups(); }
    buf = this.device.createBuffer({
      label: `storage:${name}`,
      size,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST | GPUBufferUsage.COPY_SRC | extraUsage,
    });
    this._storageBuffers.set(name, buf);
    return buf;
  }

  getStorageBuffer(name) { return this._storageBuffers.get(name) || null; }

  writeStorageBuffer(name, data, offset = 0) {
    const buf = this._storageBuffers.get(name);
    if (!buf) throw new Error(`[GPU] Storage buffer not found: ${name}`);
    this.device.queue.writeBuffer(buf, offset, data);
  }

  /** Асинхронное чтение storage-буфера на CPU через staging-буфер. */
  async readStorageBuffer(name, byteLength) {
    const src = this._storageBuffers.get(name);
    if (!src) return null;

    const size = Math.min(byteLength, src.size);
    const staging = this.device.createBuffer({
      label: `staging:${name}`,
      size,
      usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
    });

    const encoder = this.device.createCommandEncoder({ label: `read:${name}` });
    encoder.copyBufferToBuffer(src, 0, staging, 0, size);
    this.device.queue.submit([encoder.finish()]);

    try {
      await staging.mapAsync(GPUMapMode.READ);
      const copy = staging.getMappedRange().slice(0);
      staging.unmap();
      staging.destroy();
      return copy;
    } catch (e) {
      try { staging.destroy(); } catch { /* уже уничтожен */ }
      _log.warn(`[GPU] readStorageBuffer(${name}) failed:`, e.message);
      return null;
    }
  }

  // ─── Загрузка внешних изображений ──────────────────────────────

  /**
   * Копирование содержимого чужого canvas (CS3D) в текстуру.
   * Требует, чтобы у целевой текстуры были usage COPY_DST | RENDER_ATTACHMENT.
   */
  copyCanvasToTarget(sourceCanvas, targetName, format = FORMATS.LDR) {
    const w = sourceCanvas.width;
    const h = sourceCanvas.height;
    if (w === 0 || h === 0) return null;

    const target = this.ensureTarget(targetName, w, h, format);
    try {
      this.device.queue.copyExternalImageToTexture(
        // flipY не нужен: переворот делает вершинный шейдер
        { source: sourceCanvas, flipY: false },
        { texture: target.texture, premultipliedAlpha: false },
        { width: w, height: h, depthOrArrayLayers: 1 }
      );
      return target;
    } catch (e) {
      _log.warn('[GPU] copyExternalImageToTexture failed:', e.message);
      return null;
    }
  }

  /** Загрузка типизированного массива в 2D-текстуру (маски, LUT, глубина). */
  uploadData(name, data, width, height, format = 'r8unorm') {
    const bytesPerPixel = format === 'r8unorm' ? 1
      : format === 'r32float' ? 4
      : format === 'rgba8unorm' ? 4
      : format === 'rgba16float' ? 8 : 4;

    const target = this.ensureTarget(name, width, height, format);
    const bytesPerRow = Math.ceil((width * bytesPerPixel) / 256) * 256;

    // WebGPU требует выравнивание строки на 256 байт. Если исходные данные
    // упакованы плотно — перекладываем в буфер с паддингом.
    let payload = data;
    if (bytesPerRow !== width * bytesPerPixel) {
      const padded = new Uint8Array(bytesPerRow * height);
      const srcBytes = new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
      for (let y = 0; y < height; y++) {
        padded.set(
          srcBytes.subarray(y * width * bytesPerPixel, (y + 1) * width * bytesPerPixel),
          y * bytesPerRow
        );
      }
      payload = padded;
    }

    this.device.queue.writeTexture(
      { texture: target.texture },
      payload,
      { bytesPerRow, rowsPerImage: height },
      { width, height, depthOrArrayLayers: 1 }
    );
    return target;
  }

  // ─── Кадр ──────────────────────────────────────────────────────

  beginFrame() {
    if (!this.isReady()) return false;
    this._encoder = this.device.createCommandEncoder({ label: `frame:${this._frameIndex}` });
    this._stats.passes = 0;
    this._stats.dispatches = 0;
    return true;
  }

  endFrame() {
    if (!this._encoder) return;
    // Ошибки кодирования проявляются при ОТПРАВКЕ: невалидный проход делает
    // невалидным весь командный буфер, и наружу это выходит именно здесь.
    this._подЗахватом('отправка командного буфера', () => {
      this.device.queue.submit([this._encoder.finish()]);
    });
    this._encoder = null;
    this._frameIndex++;
  }

  getCanvasView() {
    try {
      return this.context.getCurrentTexture().createView();
    } catch (e) {
      _log.warn('[GPU] getCurrentTexture failed:', e.message);
      return null;
    }
  }

  // ─── Замер кадра прямо с GPU. 10.19.17 ─────────────────────────
  //
  // ПОВОД. Замер по снимку холста (drawImage → getImageData) на WebGPU может
  // вернуть пустоту: содержимое swap-chain живёт не так, как у WebGL с
  // preserveDrawingBuffer. У Андрея Вячеславовича на RTX 4060 замер показал
  // «слой 0,0% непустых пикселей и яркость 0,0», и по этим числам слой был снят
  // с экрана. Но «кадр чёрный» и «снимок не удался» — РАЗНЫЕ вещи, а вывод был
  // один. Поэтому здесь второй, независимый путь: копия текстуры холста в буфер
  // средствами самого GPU, без участия 2D-канваса.

  /** Заказать чтение пикселей холста на следующем кадре. */
  запроситьЗамерХолста() { this._замерЗапрошен = true; }

  /**
   * Вызывается ВНУТРИ кадра, после того как последний проход записал холст:
   * ставит в тот же командный буфер копию текстуры холста в staging-буфер.
   * @returns {boolean} поставлена ли копия
   */
  снятьХолстВБуфер() {
    if (!this._замерЗапрошен || !this._encoder || !this.isReady()) return false;
    let tex;
    try { tex = this.context.getCurrentTexture(); } catch (e) { _log.warn('[GPU/замер]', e.message); return false; }
    const w = tex.width, h = tex.height;
    if (!(w > 0 && h > 0)) return false;
    const bpr = Math.ceil((w * 4) / 256) * 256;
    const size = bpr * h;
    try {
      if (this._замерБуфер && this._замерБуфер.size !== size) { this._замерБуфер.destroy(); this._замерБуфер = null; }
      if (!this._замерБуфер) {
        this._замерБуфер = this.device.createBuffer({
          label: 'замер:холст', size,
          usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ,
        });
      }
      this._encoder.copyTextureToBuffer(
        { texture: tex },
        { buffer: this._замерБуфер, bytesPerRow: bpr, rowsPerImage: h },
        { width: w, height: h, depthOrArrayLayers: 1 },
      );
      this._замерМета = { w, h, bpr, формат: this.canvasFormat };
      this._замерЗапрошен = false;
      this._замерГотов = true;
      return true;
    } catch (e) {
      _log.warn('[GPU/замер] копия холста не поставлена:', e.message);
      this._замерЗапрошен = false;
      return false;
    }
  }

  /**
   * Прочитать снятую копию. Возвращает СЫРЫЕ байты и метаданные — статистику
   * считает вызывающий, чтобы порядок каналов разбирался в одном месте.
   * @returns {Promise<{данные: Uint8Array, w: number, h: number, bpr: number, формат: string}|null>}
   */
  async прочитатьЗамерХолста() {
    if (!this._замерГотов || !this._замерБуфер) return null;
    try {
      await this._замерБуфер.mapAsync(GPUMapMode.READ);
      const данные = new Uint8Array(this._замерБуфер.getMappedRange().slice(0));
      this._замерБуфер.unmap();
      this._замерГотов = false;
      return { данные, ...this._замерМета };
    } catch (e) {
      _log.warn('[GPU/замер] чтение не удалось:', e.message);
      this._замерГотов = false;
      return null;
    }
  }

  // ─── Выполнение проходов ───────────────────────────────────────

  _bindGroupKey(prefix, passName, views) {
    // Ключ по идентичности вьюх: у GPUTextureView нет id, поэтому
    // используем WeakMap-подобную нумерацию через Symbol на объекте
    let key = `${prefix}:${passName}`;
    for (const v of views) {
      if (!v) { key += '|null'; continue; }
      if (!v.__cinematicId) {
        Object.defineProperty(v, '__cinematicId', {
          value: `v${++_viewCounter}`, enumerable: false, configurable: true,
        });
      }
      key += `|${v.__cinematicId}`;
    }
    return key;
  }

  /**
   * Выполнить растровый проход.
   *
   * @param {string} passName ключ из RENDER_PASSES
   * @param {object} opts
   *   textures: { [имя из desc.textures]: GPUTextureView }
   *   uniforms: объект значений
   *   output: RenderTarget | GPUTextureView (цель)
   *   outputFormat: формат цели (обязателен, если output — вьюха)
   *   clear: очищать ли цель (по умолчанию нет — 'load')
   */
  runRenderPass(passName, opts) {
    if (!this._encoder) throw new Error('[GPU] runRenderPass outside of frame');
    if (this._захват) return this._подЗахватом(`проход ${passName}`, () => this._runRenderPassВнутри(passName, opts));
    return this._runRenderPassВнутри(passName, opts);
  }

  _runRenderPassВнутри(passName, opts) {

    const desc = RENDER_PASSES[passName];
    if (!desc) throw new Error(`[GPU] Unknown render pass: ${passName}`);

    const isTarget = opts.output && typeof opts.output === 'object' && 'view' in opts.output;
    const outputView = isTarget ? opts.output.view : opts.output;
    const outputFormat = isTarget ? opts.output.format : (opts.outputFormat || this.canvasFormat);
    if (!outputView) throw new Error(`[GPU] Pass ${passName}: no output view`);

    // Uniform-буфер
    const layout = this.getUniformLayout(passName);
    if (opts.uniforms) layout.set(opts.uniforms);
    const uniformBuffer = this._uniformPool.upload(`r:${passName}`, layout);

    // Bind group
    const views = desc.textures.map((texName) => {
      const provided = opts.textures?.[texName];
      if (!provided) {
        throw new Error(`[GPU] Pass ${passName}: missing texture "${texName}"`);
      }
      return provided;
    });

    const cacheKey = this._bindGroupKey('r', passName, views);
    let bindGroup = this._bindGroupCache.get(cacheKey);
    if (!bindGroup) {
      bindGroup = this.device.createBindGroup({
        label: `bg:r:${passName}`,
        layout: this._getRenderBindGroupLayout(passName),
        entries: [
          { binding: 0, resource: { buffer: uniformBuffer } },
          { binding: 1, resource: this.getSampler('linear') },
          { binding: 2, resource: this.getSampler('nearest') },
          ...views.map((v, i) => ({ binding: 3 + i, resource: v })),
        ],
      });
      this._bindGroupCache.set(cacheKey, bindGroup);
      this._stats.bindGroupsCreated++;
    }

    const pipeline = this.getRenderPipeline(passName, outputFormat);
    const pass = this._encoder.beginRenderPass({
      label: `pass:${passName}`,
      colorAttachments: [{
        view: outputView,
        clearValue: opts.clearValue || { r: 0, g: 0, b: 0, a: 0 },
        loadOp: opts.clear ? 'clear' : 'load',
        storeOp: 'store',
      }],
    });

    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bindGroup);
    if (opts.viewport) {
      const [x, y, w, h] = opts.viewport;
      pass.setViewport(x, y, w, h, 0, 1);
    }
    pass.draw(4, 1, 0, 0);
    pass.end();

    this._stats.passes++;
  }

  /**
   * Выполнить compute-проход.
   *
   * @param {string} passName ключ из COMPUTE_PASSES
   * @param {object} opts
   *   resources: { [имя биндинга]: GPUTextureView | GPUBuffer | GPUSampler }
   *   uniforms: объект значений
   *   dispatch: [x, y, z] в ПИКСЕЛЯХ (делится на workgroupSize автоматически)
   */
  runComputePass(passName, opts) {
    if (this._захват) return this._подЗахватом(`компьют ${passName}`, () => this._runComputePassВнутри(passName, opts));
    return this._runComputePassВнутри(passName, opts);
  }

  _runComputePassВнутри(passName, opts) {
    if (!this._encoder) throw new Error('[GPU] runComputePass outside of frame');

    const desc = COMPUTE_PASSES[passName];
    if (!desc) throw new Error(`[GPU] Unknown compute pass: ${passName}`);

    const layout = this.getUniformLayout(passName);
    if (opts.uniforms) layout.set(opts.uniforms);
    const uniformBuffer = this._uniformPool.upload(`c:${passName}`, layout);

    const resourceList = [];
    const entries = desc.bindings.map((b) => {
      if (b.kind === 'uniform') {
        return { binding: b.binding, resource: { buffer: uniformBuffer } };
      }
      const provided = opts.resources?.[b.name];
      if (!provided) throw new Error(`[GPU] Compute ${passName}: missing resource "${b.name}"`);

      resourceList.push(provided);
      if (b.kind === 'storageBuffer') {
        return { binding: b.binding, resource: { buffer: provided } };
      }
      return { binding: b.binding, resource: provided };
    });

    const cacheKey = this._bindGroupKey('c', passName, resourceList);
    let bindGroup = this._bindGroupCache.get(cacheKey);
    if (!bindGroup) {
      bindGroup = this.device.createBindGroup({
        label: `bg:c:${passName}`,
        layout: this._getComputeBindGroupLayout(passName),
        entries,
      });
      this._bindGroupCache.set(cacheKey, bindGroup);
      this._stats.bindGroupsCreated++;
    }

    const [wx, wy, wz] = desc.workgroupSize;
    const [px, py = 1, pz = 1] = opts.dispatch;
    const gx = Math.ceil(px / wx);
    const gy = Math.ceil(py / wy);
    const gz = Math.ceil(pz / wz);

    const pass = this._encoder.beginComputePass({ label: `compute:${passName}` });
    pass.setPipeline(this.getComputePipeline(passName));
    pass.setBindGroup(0, bindGroup);
    pass.dispatchWorkgroups(Math.max(1, gx), Math.max(1, gy), Math.max(1, gz));
    pass.end();

    this._stats.dispatches++;
  }

  /** Обнулить storage-буфер (перед накоплением статистики). */
  clearStorageBuffer(name, byteLength) {
    const buf = this._storageBuffers.get(name);
    if (!buf || !this._encoder) return;
    this._encoder.clearBuffer(buf, 0, Math.min(byteLength, buf.size));
  }

  /** Копирование текстуры в текстуру (для истории темпоральных эффектов). */
  copyTargetToTarget(srcName, dstName) {
    const src = this._targets.get(srcName);
    const dst = this._targets.get(dstName);
    if (!src || !dst || !this._encoder) return false;
    if (src.width !== dst.width || src.height !== dst.height) return false;

    this._encoder.copyTextureToTexture(
      { texture: src.texture },
      { texture: dst.texture },
      { width: src.width, height: src.height, depthOrArrayLayers: 1 }
    );
    return true;
  }

  // ─── Прогрев ───────────────────────────────────────────────────

  /**
   * Предсоздание пайплайнов. Первая компиляция шейдера занимает десятки
   * миллисекунд — если делать её в первом кадре, будет заметный фриз.
   */
  async warmup(renderPassNames = [], computePassNames = [], targetFormat = null) {
    const fmt = targetFormat || FORMATS.HDR;
    const tasks = [];

    for (const name of renderPassNames) {
      if (!RENDER_PASSES[name]) continue;
      tasks.push(Promise.resolve().then(() => {
        this.getRenderPipeline(name, fmt);
        if (fmt !== this.canvasFormat) this.getRenderPipeline(name, this.canvasFormat);
      }));
    }
    for (const name of computePassNames) {
      if (!COMPUTE_PASSES[name]) continue;
      tasks.push(Promise.resolve().then(() => this.getComputePipeline(name)));
    }

    const results = await Promise.allSettled(tasks);
    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length) {
      failed.forEach(f => _log.error('[GPU] Warmup failed:', f.reason?.message || f.reason));
      return Err(`${failed.length} pipeline(s) failed to compile`);
    }
    _log.debug(`[GPU] Прогрето пайплайнов: ${tasks.length}`);
    return Ok(tasks.length);
  }

  /** Проверка компиляции WGSL с человекочитаемым отчётом. */
  async validateShader(code, label = 'shader') {
    try {
      const module = this.device.createShaderModule({ label, code });
      if (typeof module.getCompilationInfo !== 'function') return Ok([]);

      const info = await module.getCompilationInfo();
      const errors = info.messages.filter(m => m.type === 'error');
      if (errors.length) {
        const text = errors
          .map(m => `  ${label}:${m.lineNum}:${m.linePos} — ${m.message}`)
          .join('\n');
        return Err(`WGSL compilation errors:\n${text}`);
      }
      return Ok(info.messages);
    } catch (e) {
      return Err(e.message);
    }
  }

  // ─── Уничтожение ───────────────────────────────────────────────

  destroy() {
    if (this._destroyed) return;
    this._destroyed = true;

    this.destroyAllTargets();
    for (const b of this._storageBuffers.values()) {
      try { b.destroy(); } catch { /* уже уничтожен */ }
    }
    this._storageBuffers.clear();

    this._uniformPool?.destroy();
    this._bindGroupCache.clear();
    this._renderPipelines.clear();
    this._computePipelines.clear();
    this._bindGroupLayouts.clear();
    this._shaderModules.clear();
    this._layouts.clear();
    this._samplers.clear();

    try { this._замерБуфер?.destroy(); } catch { /* уже уничтожен */ }
    this._замерБуфер = null;
    try { this.context?.unconfigure(); } catch { /* контекст мог не быть настроен */ }
    try { this.device?.destroy(); } catch { /* уже уничтожено */ }

    this.device = null;
    this.adapter = null;
    this.context = null;
    this._initialized = false;

    _log.debug('[GPU] Бэкенд уничтожен');
  }
}

let _viewCounter = 0;

export { RenderTarget };
